import { memo, useRef } from 'react';
import { sendEvent as sendMonetateQEvent } from '@JohnLewisPartnership/experiments-utils';

import styles from './PlpCardSizeSelector.module.scss';
import SegmentedControl from '../segmented-control/SegmentedControl';
import { usePlpCardSize } from '../../hooks/usePlpCardSize';
import {
  dispatchImmediateAnalyticsEvent,
  GLOBAL_INTERACTION,
} from '../analytics/analyticsEvents';
import { useExperiencesContext } from '../experiences/Experiences';
import classNames from 'classnames';

type Props = {
  inView?: boolean;
};

// TO DO: Delete these and import icon components from the design system
// (The Small and Large icons will be added to the design system after v23)
const SmallIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginLeft: '2px' }}
    >
      <g clipPath="url(#clip0_5964_71052)">
        <rect
          x="3.75"
          y="4"
          width="6.5"
          height="6.5"
          stroke="#141414"
          strokeWidth="1.5"
        />
        <rect
          x="13.75"
          y="4"
          width="6.5"
          height="6.5"
          stroke="#141414"
          strokeWidth="1.5"
        />
        <rect
          x="3.75"
          y="13.5"
          width="6.5"
          height="6.5"
          stroke="#141414"
          strokeWidth="1.5"
        />
        <rect
          x="13.75"
          y="13.5"
          width="6.5"
          height="6.5"
          stroke="#141414"
          strokeWidth="1.5"
        />
      </g>
      <defs>
        <clipPath id="clip0_5964_71052">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const LargeIcon = () => {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginLeft: '2px' }}
    >
      <rect
        x="4"
        y="3"
        width="16"
        height="38"
        stroke="#141414"
        strokeWidth="3"
      ></rect>
      <rect
        x="26"
        y="3"
        width="16"
        height="38"
        stroke="#141414"
        strokeWidth="3"
      ></rect>
    </svg>
  );
};

const PlpCardSizeSelector = ({ inView = true }: Props) => {
  const { isSingleColumnLayout } = useExperiencesContext();
  const { isLargeCard, onViewSelectionChange } = usePlpCardSize();
  const clickedValues = useRef(new Set<'small' | 'large'>());

  const segmentedControlOptions = [
    {
      label: 'Small',
      value: 'small',
      selected: !isLargeCard,
      IconComponent: SmallIcon,
      iconTitle: 'Small view',
    },
    {
      label: 'Large',
      value: 'large',
      selected: isLargeCard,
      IconComponent: LargeIcon,
      iconTitle: 'Large view',
    },
  ];

  const onSegmentedControlClick = (value: 'small' | 'large') => {
    onViewSelectionChange(value);

    if (!clickedValues.current.has(value)) {
      clickedValues.current.add(value);
      dispatchImmediateAnalyticsEvent(GLOBAL_INTERACTION, {
        type: 'plp',
        subType: `toggle - ${value} product card view`,
      });
    }

    const monetateEvent = `plp.cardsize.${value}.inControls${!inView ? '.scrolling' : ''}`;
    sendMonetateQEvent(monetateEvent);
  };

  return (
    <SegmentedControl
      isMultiSelect={false}
      className={classNames(styles.plpCardSizeSelector, {
        [styles['plpCardSizeSelector--singleColumn']]: isSingleColumnLayout,
      })}
      segments={segmentedControlOptions}
      callback={onSegmentedControlClick}
    />
  );
};

export default memo(PlpCardSizeSelector);
